import React, { useState } from "react";
import Swal from "sweetalert2";
import { useNavigate, Link } from "react-router-dom";
import { Form, Button, Row, Col, Card } from "react-bootstrap";
import { FaArrowLeft } from "react-icons/fa";

const API_URL = process.env.REACT_APP_API_URL;

function CreateTicketSupport() {
  const navigate = useNavigate();

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    status: "open",
    subject: "",
    assigned_to: "Admin",
    priority: "",
    description: "",
    attachment: null,
  });

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "attachment") {
      const file = files[0];
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/jpg",
      ];

      if (file && !allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          attachment: "Only PDF, JPG, JPEG, PNG files are allowed",
        }));
        return;
      }

      setFormData((prev) => ({ ...prev, attachment: file }));
      setErrors((prev) => ({ ...prev, attachment: "" }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    let newErrors = {};

    if (!formData.subject) newErrors.subject = "Subject is required";
    if (!formData.priority) newErrors.priority = "Priority is required";
    if (!formData.description)
      newErrors.description = "Description is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getAuthToken = () => localStorage.getItem("token");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    try {
      const token = getAuthToken();
      if (!token) throw new Error("Authentication token not found.");

      const sendData = new FormData();
      sendData.append("status", formData.status);
      sendData.append("subject", formData.subject);
      sendData.append("assigned_to", formData.assigned_to);
      sendData.append("priority", formData.priority);
      sendData.append("description", formData.description);

      if (formData.attachment) {
        sendData.append("attachment", formData.attachment);
      }

      const response = await fetch(`${API_URL}/create-ticket-support`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: sendData,
      });

      const result = await response.json();

      if (response.ok && result.status == "1") {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: result.message || "Support ticket created successfully",
        }).then(() => {
          navigate("/created-ticket-support-list");
        });

        setFormData({
          status: "open",
          subject: "",
          assigned_to: "Admin",
          priority: "",
          description: "",
          attachment: null,
        });
        setErrors({});
      } else {
        Swal.fire("Error", result.message || "Something went wrong", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid py-4 px-3 px-md-4">
      <Card className="shadow-sm border-0 rounded-3">
        {/* Header */}
        <Card.Header
          className="bg-gradientcolor d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 py-3 px-4 rounded-top-3"
        >
          <div className="d-flex align-items-center gap-2">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle bg-white bg-opacity-25"
              style={{ width: "38px", height: "38px" }}
            >
              <i className="fas fa-ticket-alt text-white"></i>
            </div>
            <h4 className="mb-0 text-white fw-semibold">
              Create Ticket Support
            </h4>
          </div>

          <Link
            to="/created-ticket-support-list"
            className="btn btn-light btn-sm d-inline-flex align-items-center gap-2 fw-medium shadow-sm"
          >
            <FaArrowLeft size={13} />
            <span>Back to Ticket List</span>
          </Link>
        </Card.Header>

        {/* Body */}
        <Card.Body className="p-4">
          <Form onSubmit={handleSubmit} noValidate>
            <Row className="g-3">
              {/* Subject */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    Ticket Subject <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="subject"
                    placeholder="Enter ticket subject"
                    value={formData.subject}
                    onChange={handleChange}
                    isInvalid={!!errors.subject}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.subject}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* Assign To */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    Assign To <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="assigned_to"
                    value={formData.assigned_to}
                    disabled
                  >
                    <option value="Admin">Admin</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Tickets are auto-assigned to Admin.
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Priority */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    Priority <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    isInvalid={!!errors.priority}
                  >
                    <option value="">Select Priority</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.priority}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* Status */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    Status <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    disabled
                  >
                    <option value="open">Open</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    New tickets are created with "Open" status.
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Description */}
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    Description <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    name="description"
                    rows={5}
                    placeholder="Describe your issue in detail (minimum 500 characters)..."
                    value={formData.description}
                    onChange={handleChange}
                    isInvalid={!!errors.description}
                    style={{ resize: "vertical" }}
                  />
                  <div className="d-flex justify-content-between">
                    <Form.Control.Feedback type="invalid">
                      {errors.description}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted ms-auto">
                      {formData.description.length} characters
                    </Form.Text>
                  </div>
                </Form.Group>
              </Col>

              {/* Attachment */}
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    Upload File (PDF / Image){" "}
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="file"
                    name="attachment"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleChange}
                    isInvalid={!!errors.attachment}
                  />
                  <Form.Text className="text-muted">
                    Allowed formats: PDF, JPG, JPEG, PNG
                  </Form.Text>
                  <Form.Control.Feedback type="invalid">
                    {errors.attachment}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* Buttons */}
              <Col md={12}>
                <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
                  <Link
                    to="/created-ticket-support-list"
                    className="btn btn-outline-secondary px-4"
                  >
                    Cancel
                  </Link>
                  <Button
                    type="submit"
                    disabled={loading}
                    variant="success"
                    className="px-4 d-inline-flex align-items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Creating Ticket...
                      </>
                    ) : (
                      "Create Ticket"
                    )}
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
}

export default CreateTicketSupport;